import React from 'react';
import { ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { Button, IconButton, Modal, Portal, Switch, Text } from 'react-native-paper';
import { colors, spacing, typography } from '../../../constants/theme';
import { NewQuestionForm, NewQuestionUnit, QuestionTypeOption } from '../AdminDashboard.types';

interface AdminNewQuestionModalProps {
  visible: boolean;
  form: NewQuestionForm;
  onChangeForm: (form: NewQuestionForm) => void;
  onDismiss: () => void;
  onSubmit: () => void;
}

const QUESTION_TYPES: QuestionTypeOption[] = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkboxes' },
  { value: 'number', label: 'Number' },
  { value: 'decimal', label: 'Decimal Number' },
  { value: 'plusminus', label: '+/- Number' },
  { value: 'date', label: 'Date' },
  { value: 'rating', label: 'Rating (1-5)' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'file', label: 'Photo Field' },
  { value: 'helpful_links', label: 'Helpful Links' },
  { value: 'dimensions_2d', label: 'Dimensions 2D (L x W)' },
  { value: 'dimensions_3d', label: 'Dimensions 3D (L x W x D)' },
];

const OPTION_TYPES = ['select', 'radio', 'checkbox'];

const UNIT_OPTIONS: Array<{ value: NewQuestionUnit; label: string }> = [
  { value: 'in', label: 'Inches' },
  { value: 'ft', label: 'Feet' },
  { value: 'cm', label: 'Centimeters' },
  { value: 'm', label: 'Meters' },
];

const AdminNewQuestionModal: React.FC<AdminNewQuestionModalProps> = ({
  visible,
  form,
  onChangeForm,
  onDismiss,
  onSubmit,
}) => {
  const updateForm = (updates: Partial<NewQuestionForm>) => {
    onChangeForm({ ...form, ...updates });
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={styles.modal}
      >
        <ScrollView>
          <Text style={styles.modalTitle}>Add New Question</Text>

          <View style={styles.formContainer}>
            <Text style={styles.fieldLabel}>Question Label *</Text>
            <RNTextInput
              value={form.label}
              onChangeText={(text) => updateForm({ label: text })}
              style={styles.textInputField}
              placeholder="e.g., What is the condition of the roof?"
              placeholderTextColor={colors.textSecondary}
            />

            <Text style={styles.questionTypeLabel}>Question Type *</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.typeSelector}>
              {QUESTION_TYPES.map((type) => (
                <Button
                  key={type.value}
                  mode={form.type === type.value ? 'contained' : 'outlined'}
                  onPress={() => updateForm({ type: type.value })}
                  style={styles.typeButton}
                >
                  {type.label}
                </Button>
              ))}
            </ScrollView>

            {OPTION_TYPES.includes(form.type) && (
              <>
                <Text style={styles.fieldLabel}>Options (comma-separated)</Text>
                <RNTextInput
                  value={form.options}
                  onChangeText={(text) => updateForm({ options: text })}
                  style={[styles.textInputField, styles.multilineInput]}
                  placeholder="e.g., Excellent, Good, Fair, Poor"
                  placeholderTextColor={colors.textSecondary}
                  multiline
                  numberOfLines={3}
                />
              </>
            )}

            {form.type === 'file' && (
              <>
                <View style={styles.switchContainer}>
                  <Text style={styles.fieldLabel}>Allow Multiple Photos</Text>
                  <Switch
                    value={form.multiple}
                    onValueChange={(value) => updateForm({ multiple: value })}
                  />
                </View>

                {form.multiple && (
                  <>
                    <Text style={styles.fieldLabel}>Maximum Photos</Text>
                    <RNTextInput
                      value={String(form.maxPhotos)}
                      onChangeText={(text) => {
                        const num = parseInt(text, 10) || 10;
                        updateForm({ maxPhotos: num });
                      }}
                      style={styles.textInputField}
                      keyboardType="number-pad"
                      placeholder="10"
                      placeholderTextColor={colors.textSecondary}
                    />
                  </>
                )}
              </>
            )}

            {(form.type === 'number' || form.type === 'plusminus') && (
              <>
                <View style={styles.switchContainer}>
                  <Text style={styles.fieldLabel}>Triggers Item Multiplicity</Text>
                  <Switch
                    value={form.triggersMultiplicity}
                    onValueChange={(value) => updateForm({ triggersMultiplicity: value })}
                  />
                </View>
                <Text style={styles.helperText}>
                  When enabled, entering a value {'>'} 1 will create multiple instances of this item (e.g., "How many water heaters?")
                </Text>
              </>
            )}

            {form.type === 'helpful_links' && (
              <>
                <Text style={styles.fieldLabel}>Links</Text>
                {form.links.map((link, index) => (
                  <View key={index} style={styles.linkInputRow}>
                    <View style={styles.linkInputContainer}>
                      <RNTextInput
                        value={link.label}
                        onChangeText={(text) => {
                          const links = [...form.links];
                          links[index] = { ...links[index], label: text };
                          updateForm({ links });
                        }}
                        style={[styles.textInputField, { flex: 1 }]}
                        placeholder="Display Label"
                        placeholderTextColor={colors.textSecondary}
                      />
                      <RNTextInput
                        value={link.url}
                        onChangeText={(text) => {
                          const links = [...form.links];
                          links[index] = { ...links[index], url: text };
                          updateForm({ links });
                        }}
                        style={[styles.textInputField, { flex: 2 }]}
                        placeholder="https://example.com"
                        placeholderTextColor={colors.textSecondary}
                        autoCapitalize="none"
                        keyboardType="url"
                      />
                      <IconButton
                        icon="minus-circle"
                        size={20}
                        onPress={() => {
                          if (form.links.length > 1) {
                            updateForm({
                              links: form.links.filter((_, linkIndex) => linkIndex !== index),
                            });
                          }
                        }}
                        disabled={form.links.length <= 1}
                      />
                    </View>
                  </View>
                ))}
                <Button
                  mode="text"
                  onPress={() => updateForm({ links: [...form.links, { url: '', label: '' }] })}
                  icon="plus"
                >
                  Add Link
                </Button>
              </>
            )}

            {(form.type === 'dimensions_2d' || form.type === 'dimensions_3d') && (
              <>
                <Text style={styles.fieldLabel}>Unit of Measurement</Text>
                <View style={styles.unitSelector}>
                  {UNIT_OPTIONS.map((unitOption) => (
                    <Button
                      key={unitOption.value}
                      mode={form.unit === unitOption.value ? 'contained' : 'outlined'}
                      onPress={() => updateForm({ unit: unitOption.value })}
                      style={styles.unitButton}
                    >
                      {unitOption.label}
                    </Button>
                  ))}
                </View>
              </>
            )}
          </View>

          <View style={styles.formRow}>
            <Button
              mode="outlined"
              onPress={onDismiss}
              style={styles.formField}
            >
              Cancel
            </Button>
            <Button
              mode="outlined"
              onPress={onSubmit}
              style={styles.formField}
            >
              Add Question
            </Button>
          </View>
        </ScrollView>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.background,
    margin: spacing.lg,
    padding: spacing.lg,
    borderRadius: 8,
    maxHeight: '90%',
    minHeight: '70%',
  },
  modalTitle: {
    ...typography.h2,
    marginBottom: spacing.lg,
  },
  formContainer: {
    gap: spacing.md,
  },
  formRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  formField: {
    flex: 1,
    marginBottom: spacing.md,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    paddingHorizontal: spacing.sm,
  },
  fieldLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: spacing.xs,
    color: colors.textPrimary,
  },
  helperText: {
    fontSize: 13,
    color: colors.textSecondary,
    marginTop: -spacing.xs,
    marginBottom: spacing.md,
    fontStyle: 'italic',
  },
  textInputField: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: 16,
    backgroundColor: colors.surface,
    marginBottom: spacing.md,
    minHeight: 48,
  },
  multilineInput: {
    minHeight: 80,
    textAlignVertical: 'top',
    paddingTop: spacing.sm,
  },
  questionTypeLabel: {
    marginTop: spacing.md,
    marginBottom: spacing.xs,
    fontSize: 16,
    color: colors.textPrimary,
  },
  typeSelector: {
    marginVertical: spacing.sm,
  },
  typeButton: {
    marginRight: spacing.sm,
    minWidth: 100,
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
  },
  linkInputRow: {
    marginBottom: spacing.sm,
  },
  linkInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  unitSelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  unitButton: {
    minWidth: 80,
  },
});

export default AdminNewQuestionModal;
